import { useId, useState } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Icon,
  Textarea,
} from '@speaking-coach/ui'
import type { CoooriAdmin } from './types.js'

export function DeactivateAdminControl({
  admin,
  pending,
  onDeactivate,
}: {
  admin: CoooriAdmin
  pending: boolean
  onDeactivate: (reason: string) => void
}): JSX.Element {
  const [reason, setReason] = useState('')
  const reasonId = useId()

  return (
    <AlertDialog onOpenChange={(open) => !open && setReason('')}>
      <AlertDialogTrigger asChild>
        <Button variant="dangerOpen" disabled={pending}>
          <Icon name="deactivate" /> Deactivate access
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Deactivate access for {admin.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This signs out {admin.email} immediately, disables their password and
            recovery links, and removes the identity from this directory. Audit history
            remains, and the email address can be invited again later.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="mt-4">
          <label htmlFor={reasonId} className="text-body font-medium">
            Reason for deactivation
          </label>
          <Textarea
            id={reasonId}
            className="mt-1 min-h-24"
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="For example: Left the company"
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep access</AlertDialogCancel>
          <AlertDialogAction
            disabled={pending || reason.trim() === ''}
            onClick={() => onDeactivate(reason.trim())}
          >
            Deactivate access
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export function AdminActionControls({
  admin,
  resendPending,
  cancelPending,
  onResend,
  onCancel,
}: {
  admin: CoooriAdmin
  resendPending: boolean
  cancelPending: boolean
  onResend: () => void
  onCancel: () => void
}): JSX.Element {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      {admin.canResend && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="secondary" disabled={resendPending}>
              <Icon name="resend" /> Resend
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Send a new invitation to {admin.name}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                The previous activation link will stop working. A new seven-day link
                will be emailed to {admin.email}.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep current invitation</AlertDialogCancel>
              <AlertDialogAction onClick={onResend}>
                Send new invitation
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {admin.canCancel && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="dangerOpen" disabled={cancelPending}>
              <Icon name="cancel" /> Cancel
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel {admin.name}’s invitation?</AlertDialogTitle>
              <AlertDialogDescription>
                Their activation link will stop working and the pending admin identity
                will be removed. This email address can be invited again later.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep invitation</AlertDialogCancel>
              <AlertDialogAction onClick={onCancel}>
                Cancel invitation
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  )
}
